import { Edge, FitViewOptions, Node, useReactFlow } from '@xyflow/react';
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  applyDagreLayout,
  hydrateReactFlowElements,
  transformToReactFlow,
} from '@/sections/insights/queryBuilder/components/QueryVisualizer/utils';
import type { FlatGraph } from '@/sections/insights/queryBuilder/types/FlatGraph';
import { QueryAction } from '@/sections/insights/queryBuilder/types/QueryActions';

interface UseGraphLayoutProps {
  flatGraph: FlatGraph | undefined;
  dispatch: React.Dispatch<QueryAction>;
  editable: boolean;
  expandedModalIds: Set<string>;
  toggleModalExpanded: (id: string) => void;
  setRfNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setRfEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  layoutGraphRef?: MutableRefObject<(() => void) | null>;
}

interface UseGraphLayoutReturn {
  applyLayout: () => void;
  fitViewOptions: FitViewOptions<Node>;
}

/**
 * useGraphLayout - Handles Dagre layout calculations and auto-arrange
 *
 * Provides:
 * - applyLayout function for manual layout triggering
 * - fitViewOptions for ReactFlow
 * - Automatic layout on query changes
 * - Layout ref exposure for parent components
 */
export const useGraphLayout = ({
  flatGraph,
  dispatch,
  editable,
  expandedModalIds,
  toggleModalExpanded,
  setRfNodes,
  setRfEdges,
  layoutGraphRef,
}: UseGraphLayoutProps): UseGraphLayoutReturn => {
  const { fitView } = useReactFlow();

  const fitViewOptions: FitViewOptions<Node> = useMemo(
    () => ({
      padding: {
        top: '5%',
        bottom: '80%',
        right: '20%',
        left: '20%',
      },
      duration: 300,
    }),
    []
  );

  // Imperative function to override ReactFlow positions with an explicit layout
  const applyLayout = useCallback(() => {
    const { nodes: rawNodes, edges: rawEdges } =
      transformToReactFlow(flatGraph);

    if (rawNodes.length === 0) {
      setRfNodes([]);
      setRfEdges([]);
      return;
    }

    const layoutedNodes = applyDagreLayout(rawNodes, rawEdges);
    const { nodes: finalNodes, edges: finalEdges } = hydrateReactFlowElements(
      layoutedNodes,
      rawEdges,
      editable,
      expandedModalIds,
      toggleModalExpanded,
      flatGraph?.rootId
    );

    setRfNodes(finalNodes);
    setRfEdges(finalEdges);

    // Persist Dagre-calculated positions to flatGraph
    layoutedNodes.forEach(node => {
      dispatch({
        type: 'UPDATE_NODE_POSITION',
        nodeId: node.id,
        position: node.position,
      });
    });
  }, [
    flatGraph,
    editable,
    expandedModalIds,
    toggleModalExpanded,
    dispatch,
    setRfNodes,
    setRfEdges,
  ]);

  // Expose applyLayout function via ref (for Auto Arrange button)
  // This triggers Dagre layout AND fitView
  useEffect(() => {
    if (layoutGraphRef) {
      layoutGraphRef.current = () => {
        applyLayout();
        setTimeout(() => fitView(fitViewOptions), 50); // Delay to allow DOM update
      };
    }
  }, [applyLayout, layoutGraphRef, fitView, fitViewOptions]);

  // Initial layout on query load (detected by flatGraph.rootId change)
  const previousQueryIdRef = useRef<string | undefined>(undefined);
  const shouldApplyLayoutRef = useRef(false);

  // Detect query changes and flag for layout
  useEffect(() => {
    // Use flatGraph.rootId as the stable identifier for the query
    const currentQueryId = flatGraph?.rootId;

    // Trigger layout only when query actually changes (not on every render)
    if (currentQueryId && currentQueryId !== previousQueryIdRef.current) {
      shouldApplyLayoutRef.current = true;
      previousQueryIdRef.current = currentQueryId;
    }
  }, [flatGraph?.rootId]);

  // Apply layout after render when flagged (avoids setState in effect)
  useEffect(() => {
    if (shouldApplyLayoutRef.current) {
      shouldApplyLayoutRef.current = false;
      // Use queueMicrotask to defer execution after render
      queueMicrotask(() => {
        applyLayout();
        setTimeout(() => fitView(fitViewOptions), 50);
      });
    }
  }, [applyLayout, fitView, fitViewOptions]);

  return {
    applyLayout,
    fitViewOptions,
  };
};
